import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/Pills";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, ShieldCheck, AlertTriangle, Phone, GitMerge, Trash2, Check } from "lucide-react";

function DuplicateGroup({ group, onChanged }) {
  const navigate = useNavigate();
  const [primary, setPrimary] = useState(group.customers[0]?.id);
  const [busy, setBusy] = useState(false);
  const [confirmMerge, setConfirmMerge] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const primaryCust = group.customers.find((c) => c.id === primary);
  const others = group.customers.filter((c) => c.id !== primary);

  const doMerge = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/merge-customers", {
        primary_id: primary,
        duplicate_ids: others.map((c) => c.id),
      });
      toast.success(`Merged ${r.data.removed} record(s) into ${primaryCust?.business_name || "the kept record"}`);
      setConfirmMerge(false);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (c) => {
    setBusy(true);
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success(`Deleted "${c.business_name}"`);
      setConfirmDelete(null);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="overflow-hidden" data-testid={`dup-group-${group.mobile}`}>
      <div className="flex items-center gap-2 px-5 py-3 bg-slate-50 border-b">
        <Phone size={15} className="text-slate-400" />
        <span className="font-mono font-semibold text-slate-900">{group.mobile}</span>
        <span className="ml-auto text-xs font-medium bg-rose-100 text-rose-700 rounded-full px-2.5 py-0.5">{group.count} records</span>
      </div>

      <div className="divide-y divide-slate-100">
        {group.customers.map((c) => {
          const isPrimary = c.id === primary;
          return (
            <div key={c.id} className={`flex items-center gap-3 px-5 py-3 text-sm ${isPrimary ? "bg-emerald-50/60" : ""}`} data-testid={`dup-cust-${c.id}`}>
              <button
                onClick={() => setPrimary(c.id)}
                className={`shrink-0 h-5 w-5 rounded-full border flex items-center justify-center transition-colors ${isPrimary ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 text-transparent hover:border-emerald-400"}`}
                title="Keep this record"
                data-testid={`dup-keep-${c.id}`}
              >
                <Check size={12} strokeWidth={3} />
              </button>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/app/customers/${c.id}`)}>
                <div className="font-medium text-slate-800 truncate flex items-center gap-2">
                  {c.business_name}
                  {isPrimary && <span className="text-[10px] font-semibold bg-emerald-600 text-white rounded px-1.5 py-0.5">KEEP</span>}
                </div>
                <div className="text-xs text-slate-400">RM: {c.rm_name || c.rm_username} · {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</div>
              </div>
              <StatusPill value={c.lead_status} />
              <button
                onClick={() => setConfirmDelete(c)}
                disabled={busy}
                className="shrink-0 text-slate-300 hover:text-rose-600 transition-colors disabled:opacity-40"
                title="Delete this record"
                data-testid={`dup-delete-${c.id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 px-5 py-3 bg-slate-50 border-t">
        <div className="text-xs text-slate-500 flex-1">
          Merging keeps the checked record and folds the other {others.length} into it — furthest lead stage, all documents, follow-ups and activity history are preserved.
        </div>
        <Button size="sm" onClick={() => setConfirmMerge(true)} disabled={busy || others.length === 0} data-testid={`dup-merge-${group.mobile}`}>
          <GitMerge size={14} className="mr-1.5" /> Merge {others.length} into kept
        </Button>
      </div>

      <AlertDialog open={confirmMerge} onOpenChange={setConfirmMerge}>
        <AlertDialogContent data-testid="dup-merge-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Merge duplicate records?</AlertDialogTitle>
            <AlertDialogDescription>
              This keeps <b>{primaryCust?.business_name}</b> and permanently removes the other {others.length} record(s) for
              mobile <b>{group.mobile}</b>. Documents, follow-ups and activity from all records are combined into the kept profile. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doMerge(); }} disabled={busy} data-testid="dup-merge-confirm-btn">
              {busy ? "Merging…" : "Merge records"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="dup-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete <b>{confirmDelete?.business_name}</b> (RM: {confirmDelete?.rm_name || confirmDelete?.rm_username}). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doDelete(confirmDelete); }} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="dup-delete-confirm-btn">
              {busy ? "Deleting…" : "Delete record"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default function Duplicates() {
  const navigate = useNavigate();
  const [data, setData] = useState({ groups: [], total_duplicates: 0 });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/duplicate-mobiles")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="duplicates-page">
      <button onClick={() => navigate("/app/customers")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3" data-testid="dup-back-btn">
        <ArrowLeft size={15} /> Back to Customer Database
      </button>
      <PageHeader title="Duplicate Finder" subtitle="Records sharing the same mobile number. Merge them into one clean profile or delete extras." />

      {loading ? (
        <div className="p-10 text-center text-slate-400 animate-pulse">Scanning records…</div>
      ) : data.total_duplicates === 0 ? (
        <Card className="p-12 text-center" data-testid="dup-empty">
          <ShieldCheck size={44} className="mx-auto text-emerald-500 mb-3" />
          <div className="font-semibold text-slate-800">No duplicate mobile numbers found</div>
          <div className="text-sm text-slate-400 mt-1">Your database is clean — every mobile is unique.</div>
        </Card>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4 text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-amber-800" data-testid="dup-count-banner">
            <AlertTriangle size={16} />
            <b>{data.total_duplicates}</b> mobile number{data.total_duplicates > 1 ? "s are" : " is"} used by more than one record. Pick the one to keep, then merge or delete the rest.
          </div>
          <div className="space-y-4">
            {data.groups.map((g) => (
              <DuplicateGroup key={g.mobile} group={g} onChanged={load} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
